import { useState, useCallback, useEffect, useRef } from 'react';
import { ref as dbRef, update } from 'firebase/database';
import { db } from '../../lib/firebase';
import { DETAILED_STEPS } from '../../components/estimation/detailed/config';
import { calculateDetailedEstimate } from '../../utils/detailedestimation/calculator';
import { updateEstimate } from '../../services/estimateService';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const useDetailedEstimate = (
  initialData?: any,
  isEditMode: boolean = false,
  estimateId?: string,
  userId?: string
) => {
  const [currentStep, setCurrentStep] = useState(
    initialData ? DETAILED_STEPS[1].id : DETAILED_STEPS[0].id
  );
  const [formData, setFormData] = useState(initialData || {});
  const [completedSteps, setCompletedSteps] = useState<string[]>(
    initialData ? ['description'] : []
  );
  const [error, setError] = useState<string | null>(null);
  const [draftEstimateId, setDraftEstimateId] = useState<string | null>(estimateId ?? null);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // The scrollable container is <main> in AuthenticatedLayout (overflow-y-auto),
    // not the window — so window.scrollTo has no effect.
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'auto' });
    else window.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentStep]);

  // Autosave with 1500ms debounce
  useEffect(() => {
    if (!draftEstimateId || !userId || currentStep === 'description') return;

    const hasData = Object.entries(formData).some(([key, value]) => {
      if (key === 'description' || key === 'discounts') return false;
      return (
        value &&
        typeof value === 'object' &&
        Object.values(value as Record<string, any>).some(
          (item: any) => item?.selected === true
        )
      );
    });

    if (!hasData) return;

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);

    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        await update(dbRef(db, `estimates/${userId}/${draftEstimateId}`), {
          [currentStep]: formData[currentStep],
          updatedAt: new Date().toISOString(),
        });
        setAutosaveStatus('saved');
        idleTimer.current = setTimeout(() => setAutosaveStatus('idle'), 2000);
      } catch {
        setAutosaveStatus('error');
        idleTimer.current = setTimeout(() => setAutosaveStatus('idle'), 2000);
      }
    }, 1500);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [formData, currentStep, draftEstimateId, userId]); // eslint-disable-line

  const handleStepDataChange = useCallback((stepId: string, data: any) => {
    if (isEditMode && initialData && stepId === 'description') return;
    setFormData((prev: any) => ({ ...prev, [stepId]: data }));
    setError(null);
  }, [initialData, isEditMode]);

  const handleStepClick = useCallback((stepId: string) => {
    // Block navigation to description once completed (edit mode or already validated)
    if (
      ((isEditMode && initialData) || completedSteps.includes('description')) &&
      stepId === 'description'
    ) {
      return;
    }
    // Free navigation on all other steps
    setCurrentStep(stepId);
  }, [completedSteps, initialData, isEditMode]);

  const handleNext = useCallback(() => {
    const currentIndex = DETAILED_STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex === DETAILED_STEPS.length - 1) {
      try {
        calculateDetailedEstimate(formData);
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    }

    if (currentIndex < DETAILED_STEPS.length - 1) {
      setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
      setCurrentStep(DETAILED_STEPS[currentIndex + 1].id);
      return true;
    }
    return false;
  }, [currentStep, formData]);

  const handleBack = useCallback(() => {
    const currentIndex = DETAILED_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex > 1 || (currentIndex === 1 && !completedSteps.includes('description'))) {
      setCurrentStep(DETAILED_STEPS[currentIndex - 1].id);
      return true;
    }
    return false;
  }, [currentStep, completedSteps]);

  const calculateEstimate = useCallback(() => {
    try {
      return calculateDetailedEstimate(formData);
    } catch (err) {
      console.error('Erreur lors du calcul:', err);
      return null;
    }
  }, [formData]);

  return {
    currentStep,
    formData,
    completedSteps,
    error,
    draftEstimateId,
    setDraftEstimateId,
    autosaveStatus,
    handleStepClick,
    handleNext,
    handleBack,
    handleStepDataChange,
    calculateEstimate,
  };
};
